import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  useReactTable,
} from '@tanstack/react-table'
import {
  ArchiveX,
  CheckIcon,
  ChevronDown,
  ExternalLink,
  Search,
  Send,
} from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { CreateOrEditEnrollmentEvent, CreateOrEditEnrollmentEventProvider, useCreateOrEditEnrollmentEvent } from 'components/CreateOrEditEnrollmentEvent'
import { brokersMembers } from 'rails_routes'
import { Button, Tooltip } from 'slab'
import { type Employee } from 'types'
import { useWindowLocation } from 'utils/windowLocation'

import EmployeesTableLoadingSkeleton from '../EmployeesTableLoadingSkeleton'
import { useGetBrokersEmployees } from '../hooks/useGetBrokersEmployees'
import { SelectPlanYear } from '../SelectPlanYearDropdown'
import EmployeeStillOnboardingDialog from './EmployeeStillOnboardingDialog'

type BrokersEmployeeTableProps = {
  employerId: string
  planYearsAvailable: number[]
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isDeniedEmployer: boolean
  hasInvitedEmployeesBefore: boolean
  hasMembersWithActiveCoverageThisYear: boolean
  expiredStatusEnabled: boolean
}

const BrokerEmployeesTableImpl = ({
  employerId,
  planYearsAvailable,
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  hasInvitedEmployeesBefore,
  expiredStatusEnabled
}: BrokersEmployeeTableProps) => {
  const { assign } = useWindowLocation()

  const [selectedRows, setSelectedRows] = useState([] as any[])
  const [
    openEmployeeStillOnboardingDialog,
    setOpenEmployeeStillOnboardingDialog,
  ] = useState(false)

  const [notInvitedChecked, setNotInvitedChecked] = useState(false)
  const [invitedChecked, setInvitedChecked] = useState(false)
  const [invitePendingChecked, setInvitePendingChecked] = useState(false)
  const [inProgressChecked, setInProgressChecked] = useState(false)
  const [enrolledChecked, setEnrolledChecked] = useState(false)
  const [declinedChecked, setDeclinedChecked] = useState(false)
  const [pendingTerminationChecked, setPendingTerminationChecked] =
    useState(false)
  const [cobraChecked, setCobraChecked] = React.useState(false)
  const [terminationScheduledChecked, setTerminationScheduledChecked] =
    React.useState(false)
  const [terminatedChecked, setTerminatedChecked] = useState(false)
  const [expiredChecked, setExpiredChecked] = useState(false)

  const [selectedPlanYear, setSelectedPlanYear] = React.useState<number>(
    planYearsAvailable[0]
  )

  const { data: employeesData, isLoading, refetch: refetchEmployees } = useGetBrokersEmployees({ employerId, planYear: selectedPlanYear })

  const columns: ColumnDef<Employee>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <input
          id='select-all-checkbox'
          type='checkbox'
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
          }
          onChange={() => {
            if (table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()) {
              table.toggleAllPageRowsSelected(false)
              setSelectedRows([])
            } else {
              const invitableEmployees = table.getRowModel().rows.reduce(
                (acc, row) => {
                  if (row.getValue('enrollment_status') === 'not_invited' && selectedPlanYear >= new Date().getFullYear()) {
                    acc.push(row)
                    row.toggleSelected(true)
                  }

                  return acc
                }, [] as Row<Employee>[]
              )
              setSelectedRows(invitableEmployees)
            }
          }}
          aria-label='Select all'
          className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple-secondary focus:ring-thatch-purple-secondary disabled:bg-slate-100'
        />
      ),
      cell: ({ row }) => {
        const disabled = row.getValue('enrollment_status') !== 'not_invited' || selectedPlanYear < new Date().getFullYear()

        return (
          <input
            name='select-checkbox'
            type='checkbox'
            checked={row.getIsSelected()}
            disabled={disabled}
            onChange={() => {
              if (row.getIsSelected() === true) {
                row.toggleSelected(false)
                setSelectedRows(
                  selectedRows.filter(
                    (selectedRow) => selectedRow.id !== row.id
                  )
                )
              } else {
                row.toggleSelected(true)
                setSelectedRows([...selectedRows, row])
              }
            }}
            aria-label='Select row'
            className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple-secondary focus:ring-thatch-purple-secondary disabled:bg-slate-100'
          />
        )
      },
    },
    {
      accessorKey: 'employee_name',
      header: 'Employee',
      accessorFn: (row: any) => {
        return `${row.first_name} ${row.last_name}`
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'start_date',
      header: 'Start date',
      cell: ({ row }) => {
        if (
          row.getValue('start_date') === null ||
          row.getValue('start_date') === undefined
        ) {
          return <div>---</div>
        }

        const dateStr: string = row.getValue('start_date')
        const [year, month, day] = dateStr.split('T')[0].split('-')
        const date: Date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10)
        )

        const formatter = new Intl.DateTimeFormat('en-US', {
          month: 'short', // "short" for abbreviated month name
          day: '2-digit',
          year: 'numeric',
        })

        const formattedDate = formatter.format(date)

        return <div>{formattedDate}</div>
      },
    },
    {
      accessorKey: 'enrollment_status',
      filterFn: (row, _columnId, filterValuesList) => {
        if (filterValuesList.length > 0) {
          return filterValuesList.includes(row.getValue('enrollment_status'))
        } else {
          return true
        }
      },
      header: () => {
        return (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button aria-label='Customise options'>
                <div className='flex items-center space-x-1 hover:cursor-pointer'>
                  <span>Status</span>
                  <ChevronDown size={16} />
                </div>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className='DropdownMenuContent'
                sideOffset={5}
              >
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={notInvitedChecked}
                  onCheckedChange={setNotInvitedChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Not Invited
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={invitePendingChecked}
                  onCheckedChange={setInvitePendingChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Invite Pending
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={invitedChecked}
                  onCheckedChange={setInvitedChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Invited
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={inProgressChecked}
                  onCheckedChange={setInProgressChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Viewing plans
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={enrolledChecked}
                  onCheckedChange={setEnrolledChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Enrolled
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={declinedChecked}
                  onCheckedChange={setDeclinedChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Declined
                </DropdownMenu.CheckboxItem>
                {expiredStatusEnabled && (
                  <DropdownMenu.CheckboxItem
                    className='DropdownMenuCheckboxItem'
                    checked={expiredChecked}
                    onCheckedChange={setExpiredChecked}
                  >
                    <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                      <CheckIcon size={16} color='#6941C6' />
                    </DropdownMenu.ItemIndicator>
                    Invite expired
                  </DropdownMenu.CheckboxItem>)}
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={pendingTerminationChecked}
                  onCheckedChange={setPendingTerminationChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Payroll Deactivated
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={cobraChecked}
                  onCheckedChange={setCobraChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  COBRA
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={terminationScheduledChecked}
                  onCheckedChange={setTerminationScheduledChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Offboarding Scheduled
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.CheckboxItem
                  className='DropdownMenuCheckboxItem'
                  checked={terminatedChecked}
                  onCheckedChange={setTerminatedChecked}
                >
                  <DropdownMenu.ItemIndicator className='DropdownMenuItemIndicator'>
                    <CheckIcon size={16} color='#6941C6' />
                  </DropdownMenu.ItemIndicator>
                  Offboarded
                </DropdownMenu.CheckboxItem>
                <DropdownMenu.Arrow className='DropdownMenuArrow' />
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )
      },
      cell: ({ row }) => {
        let classes = ''
        let label = ''
        let statusTip = ''

        switch (row.getValue('enrollment_status')) {
          case 'not_invited':
            classes = 'bg-yellow-100 text-yellow-800'
            label = 'Not Invited'
            statusTip =
              'This employee has not received an email inviting them to begin benefits selection'
            break
          case 'invite_pending':
            classes = 'bg-fuchsia-100 text-fuchsia-600'
            label = 'Invite Pending'
            statusTip =
              'This employee will be sent an invitation email when the enrollment window begins'
            break
          case 'invited':
            classes = 'bg-fuchsia-100 text-fuchsia-600'
            label = 'Invited'
            statusTip =
              'This employee has been sent an invitation email but has not setup their Thatch account'
            break
          case 'in_progress':
            classes = 'bg-cyan-100 text-cyan-800'
            label = 'Viewing plans'
            statusTip =
              'This employee has setup their Thatch account and is viewing their health benefit options'
            break
          case 'enrolled':
            classes = 'bg-green-100 text-green-700'
            label = 'Enrolled'
            statusTip =
              'This employee has made their benefits selections and are now enrolled in their plans'
            break
          case 'declined':
            classes = 'bg-gray-100 text-gray-600'
            label = 'Declined'
            statusTip = 'This employee has declined their health benefits'
            break
          case 'payroll_deactivated':
            classes = 'bg-orange-100 text-orange-800'
            label = 'Payroll Deactivated'
            statusTip =
              "Please verify the employee's termination details and submit the termination. We will notify the employee of their next steps."
            break
          case 'cobra':
            classes = 'bg-purple-100 text-purple-800'
            label = 'COBRA'
            statusTip = 'This employee has accepted their COBRA election.'
            break
          case 'termination_scheduled':
            classes = 'bg-gray-100 text-gray-600'
            label = 'Offboarding Scheduled'
            statusTip =
              'This employee has been terminated with a future effective date. They will be terminated EOD of the effective date.'
            break
          case 'terminated':
            classes = 'bg-gray-100 text-gray-600'
            label = 'Offboarded'
            statusTip = 'This employee is no longer employed at your company'
            break
          case 'expired':
            if (expiredStatusEnabled) {
              classes = 'bg-gray-100 text-gray-600'
              label = 'Invite expired'
              statusTip = "This employee didn't decline or enroll in coverage during their enrollment window."
            } else {
              classes = 'bg-gray-100 text-gray-800'
              label = 'Unknown'
              statusTip = "This employee's current status is unknown. Please reach out to support@thatch.com"
            }
            break
          default:
            classes = 'bg-gray-100 text-gray-800'
            label = 'Unknown'
            statusTip =
              "This employee's current status is unknown. Please reach out to support@thatch.com"
        }
        return (
          <Tooltip content={statusTip}>
            <div
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
            >
              {label}
            </div>
          </Tooltip>
        )
      },
    },
    {
      accessorKey: 'onboard_link',
      header: 'Onboard',
      cell: ({ row }) => {
        const onboardLink = row.getValue('onboard_link')
        const disabled = [
          'enrolled',
          'pending_termination',
          'terminated',
          'cobra',
        ].includes(row.getValue('enrollment_status'))

        if (!isFullyOnboarded || !isFullyOnboardedWithBankAccount || isDeniedEmployer) {
          return (
            <OnboardEmployeeTooltip
              isFullyOnboarded={isFullyOnboarded}
              isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
              isDeniedEmployer={isDeniedEmployer}
            >
              <Button
                disabled
                leftIcon={ExternalLink}
                variant='tertiary-deemphasized'
                onClick={(e) => handleOnboardLinkClick(e, onboardLink)}
              >
                Onboard
              </Button>
            </OnboardEmployeeTooltip>
          )
        }

        return (
          <Button
            disabled={disabled}
            leftIcon={ExternalLink}
            variant='tertiary-deemphasized'
            onClick={(e) => handleOnboardLinkClick(e, onboardLink)}
          >
            Onboard
          </Button>
        )
      },
    },
    {
      accessorKey: 'terminate_link',
      header: () => {
        return <div className='text-center'>Offboard</div>
      },
      cell: ({ row }) => {
        const terminateLink = row.getValue('terminate_link') as string

        if (
          row.getValue('enrollment_status') === 'terminated' ||
          row.getValue('enrollment_status') === 'termination_scheduled' ||
          row.getValue('enrollment_status') === 'cobra' || isDeniedEmployer
        ) {
          return (
            <ArchiveX
              className='w-full text-center cursor-not-allowed'
              size={20}
              color='#9ca3af'
              data-testid={`offboard-${row.id}`}
            />
          )
        }

        return (
          <ArchiveX
            className='w-full text-center cursor-pointer'
            size={20}
            color='#f87171'
            onClick={(e) => handleTerminateClick(e, terminateLink)}
          />
        )
      },
    },
  ]

  const handleOnboardLinkClick = (event: any, onboardLink: any) => {
    event.stopPropagation()
    assign(onboardLink)
  }

  const handleTerminateClick = (event: any, terminateLink: string) => {
    event.stopPropagation()
    assign(terminateLink)
  }

  const data = employeesData?.data.employees || []

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  useEffect(() => {
    const filters = []

    if (notInvitedChecked) filters.push('not_invited')
    if (invitePendingChecked) filters.push('invite_pending')
    if (invitedChecked) filters.push('invited')
    if (inProgressChecked) filters.push('in_progress')
    if (enrolledChecked) filters.push('enrolled')
    if (declinedChecked) filters.push('declined')
    if (pendingTerminationChecked) filters.push('payroll_deactivated')
    if (cobraChecked) filters.push('cobra')
    if (terminationScheduledChecked) filters.push('termination_scheduled')
    if (terminatedChecked) filters.push('terminated')
    if (expiredChecked && expiredStatusEnabled) filters.push('expired')

    table.getColumn('enrollment_status')?.setFilterValue(filters)
  }, [
    table,
    notInvitedChecked,
    invitePendingChecked,
    invitedChecked,
    inProgressChecked,
    enrolledChecked,
    declinedChecked,
    pendingTerminationChecked,
    cobraChecked,
    terminationScheduledChecked,
    terminatedChecked,
    expiredChecked
  ])

  if (isLoading) return <EmployeesTableLoadingSkeleton />

  return (
    <div>
      <CreateOrEditEnrollmentEvent
        hasInvitedEmployeesBefore={hasInvitedEmployeesBefore}
        selectedPlanYear={selectedPlanYear}
        resetSelectedRowIds={() => {
          table.toggleAllPageRowsSelected(false)
          setSelectedRows([])
        }}
        fetchEmployees={refetchEmployees}
        employerId={employerId}
        apiType='brokers'
      />
      <div className='flex items-center justify-between mb-3 w-full'>
        <div className='flex items-center'>
          <SelectPlanYear
            planYearsAvailable={planYearsAvailable}
            selectedPlanYear={selectedPlanYear}
            handleSetPlanYear={(value: string) => {
              table.toggleAllPageRowsSelected(false)
              setSelectedRows([])
              setSelectedPlanYear(parseInt(value))
            }}
          />
          <div className='relative rounded-md shadow-sm h-10'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <Search size={20} color='#9ca3af' />
            </div>
            <input
              id='search-employees'
              placeholder='Find employees'
              onChange={(event) => {
                table
                  .getColumn('employee_name')
                  ?.setFilterValue(event.target.value)
              }}
              className='input-default pl-10 h-10 rounded-lg'
            />
          </div>
        </div>
        <div>
          <InviteEmployeeButton
            isFullyOnboarded={isFullyOnboarded}
            isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
            isDeniedEmployer={isDeniedEmployer}
            selectedRows={selectedRows}
          />
        </div>
      </div>
      <div className='-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8'>
        <div className='inline-block min-w-full py-2 align-middle md:px-6 lg:px-8'>
          <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
            <table
              className='min-w-full divide-y divide-gray-200'
              id='employees-table'
            >
              <thead className='bg-smoke'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className='py-3 px-2 text-left text-sm font-normal text-slate-700 sm:px-4'
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className='hover:cursor-pointer hover:bg-gray-50'
                    onClick={(event) => {
                      const target = event.target as HTMLElement
                      // If the click comes from the first element in the row (the invite checkbox), stop propagation.
                      if (target.getAttribute('name') === 'select-checkbox') {
                        event.stopPropagation()
                      } else {
                        const memberId = (row.original as { member_id: string })
                          .member_id

                        if (memberId === null) {
                          setOpenEmployeeStillOnboardingDialog(true)
                        } else {
                          assign(brokersMembers.show({ id: memberId, query: { selected_year: selectedPlanYear } }))
                        }
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className='whitespace-nowrap p-2 text-sm text-gray-600 sm:px-4'
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className='flex items-center justify-between mt-2 mb-4 px-4'>
        <div className='text-thatch-gray-secondary text-sm'>
          {table.getPrePaginationRowModel().rows.length} employees
        </div>
        <div className='flex gap-2 mt-2 text-sm text-thatch-gray-secondary'>
          <button
            className='pagination-button'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <button
            className='pagination-button'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
          <span className='flex items-center gap-1 text-sm'>
            <div>Page</div>
            <div>
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </div>
          </span>
        </div>
      </div>
      <EmployeeStillOnboardingDialog
        openDialog={openEmployeeStillOnboardingDialog}
      />
    </div>
  )
}

const OnboardEmployeeTooltip = ({
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  children,
}: {
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isDeniedEmployer: boolean
  children: React.ReactNode
}) => {
  let tooltipText = ''
  if (!isFullyOnboarded) {
    tooltipText = 'Employers must complete onboarding to add and invite employees'
  } else if (!isFullyOnboardedWithBankAccount) {
    tooltipText = 'Employers must have a verified bank account to invite employees'
  } else if (isDeniedEmployer) {
    tooltipText = 'Onboarding employees is disabled. Please reach out to support@thatch.com for help.'
  }

  return (
    <Tooltip content={tooltipText}>
      <div className='inline-flex'>{children}</div>
    </Tooltip>
  )
}

const InviteEmployeeTooltip = ({
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  children,
}: {
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isDeniedEmployer: boolean
  selectedRows: any[]
  children: React.ReactNode
}) => {
  let tooltipText = 'Please select an employee to invite them'
  if (!isFullyOnboarded) {
    tooltipText = 'Employers must complete onboarding to add and invite employees'
  } else if (!isFullyOnboardedWithBankAccount) {
    tooltipText = 'Employers must have a verified bank account to invite employees'
  } else if (isDeniedEmployer) {
    tooltipText = 'Inviting employees is disabled. Please reach out to support@thatch.com for help.'
  }

  return (
    <Tooltip content={tooltipText}>
      {children}
    </Tooltip>
  )
}

const InviteEmployeeButton = ({
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  selectedRows,
}: {
  isFullyOnboarded: boolean
  isFullyOnboardedWithBankAccount: boolean
  isDeniedEmployer: boolean
  selectedRows: any[]
}) => {
  const { openCreateOrEditEnrollmentEvent } = useCreateOrEditEnrollmentEvent()

  if (!isFullyOnboarded || !isFullyOnboardedWithBankAccount || isDeniedEmployer || selectedRows.length <= 0) {
    return (
      <InviteEmployeeTooltip
        isFullyOnboarded={isFullyOnboarded}
        isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
        isDeniedEmployer={isDeniedEmployer}
        selectedRows={selectedRows}
      >
        <button className='button-disabled'>
          <Send className='mr-2' size={16} />
          Invite selected{' '}
          {selectedRows.length > 0 && `(${selectedRows.length})`}
        </button>
      </InviteEmployeeTooltip>
    )
  }

  return (
    <button
      className='button-outline w-auto shrink-0'
      onClick={() => openCreateOrEditEnrollmentEvent(selectedRows.map((row) => row.original))}
    >
      <Send className='mr-2' size={16} />
      Invite selected{' '}
      {selectedRows.length > 0 && `(${selectedRows.length})`}
    </button>
  )
}

const BrokerEmployeesTable = ({
  employerId,
  planYearsAvailable,
  isFullyOnboarded,
  isFullyOnboardedWithBankAccount,
  isDeniedEmployer,
  hasMembersWithActiveCoverageThisYear,
  hasInvitedEmployeesBefore,
  expiredStatusEnabled
}: BrokersEmployeeTableProps) => {
  return (
    <CreateOrEditEnrollmentEventProvider>
      <BrokerEmployeesTableImpl
        employerId={employerId}
        planYearsAvailable={planYearsAvailable}
        isFullyOnboarded={isFullyOnboarded}
        isFullyOnboardedWithBankAccount={isFullyOnboardedWithBankAccount}
        isDeniedEmployer={isDeniedEmployer}
        hasInvitedEmployeesBefore={hasInvitedEmployeesBefore}
        hasMembersWithActiveCoverageThisYear={hasMembersWithActiveCoverageThisYear}
        expiredStatusEnabled={expiredStatusEnabled}
      />
    </CreateOrEditEnrollmentEventProvider>
  )
}

export default BrokerEmployeesTable
